// Per-year adjustment engine for OP_03.
// Composes on top of the module's adjusted monthly projection so results,
// waterfalls, constraint feeds, staged rows, and snapshots all inherit the
// year-by-year edits from one place.

import { resolveForecastQuarterAdjustment } from './forecastQuarterlyGrowth';
import { FORECAST_YEARS } from './forecastProjection';

export const ADJUSTMENT_YEARS = FORECAST_YEARS;

// Additive / pts-based per-year levers. value arrays align to ADJUSTMENT_YEARS.
export const YEAR_LEVER_DEFS = [
  { id: 'loanGrowthPts', label: 'Loan growth adj', unit: 'pts', step: 0.25, min: -5, max: 5, group: 'Balance sheet', glGroup: 'Loans' },
  { id: 'depositGrowthPts', label: 'Deposit growth adj', unit: 'pts', step: 0.25, min: -5, max: 5, group: 'Balance sheet', glGroup: 'Transaction Deposits' },
  { id: 'feeAdjMM', label: 'Other NIR adj', unit: '$MM/yr', step: 0.5, min: -60, max: 60, group: 'Non-interest revenue', glGroup: 'Other Non-Interest Income' },
  { id: 'expenseAdjMM', label: 'Operating expense adj', unit: '$MM/yr', step: 0.5, min: -80, max: 80, group: 'Non-interest expense', glGroup: 'Other Operating Expense' },
  { id: 'compAdjMM', label: 'Compensation adj', unit: '$MM/yr', step: 0.5, min: -60, max: 60, group: 'Non-interest expense', glGroup: 'Personnel Compensation' },
  { id: 'provisionAdjMM', label: 'Provision adj', unit: '$MM/yr', step: 0.5, min: -40, max: 40, group: 'Credit', glGroup: 'Provision for Credit Losses' }
];

export const DEFAULT_YEAR_ADJUSTMENTS = Object.fromEntries(
  YEAR_LEVER_DEFS.map((def) => [def.id, ADJUSTMENT_YEARS.map(() => 0)])
);

// Existing-workforce levers, set per fiscal year. Coefficients mirror the
// year-1 model engine; the model reads baselines so this layer owns the effect.
export const WORKFORCE_YEAR_DEFS = [
  { id: 'meritIncreasePct', label: 'Merit / wage increase', base: 3.5, current: 3.6, step: 0.1, min: 0, max: 7 },
  { id: 'incentiveAccrualPct', label: 'Incentive accrual', base: 18.0, current: 18.5, step: 0.5, min: 8, max: 30 },
  { id: 'productivityLiftPct', label: 'Existing banker productivity lift', base: 1.5, current: 1.7, step: 0.1, min: -4, max: 6 },
  { id: 'loanRetentionPct', label: 'Existing loan book retention', base: 94.0, current: 94.5, step: 0.25, min: 85, max: 99 },
  { id: 'depositRetentionPct', label: 'Existing deposit book retention', base: 92.0, current: 92.4, step: 0.25, min: 82, max: 99 }
];

export const DEFAULT_WORKFORCE_YEARS = Object.fromEntries(
  WORKFORCE_YEAR_DEFS.map((def) => [def.id, ADJUSTMENT_YEARS.map(() => def.current)])
);

const workforceYearEffects = (workforceYears, yearIndex, compBase) => {
  if (!workforceYears) return { expense: 0, loanPts: 0, depositPts: 0 };
  const get = (id, base) => {
    const values = workforceYears[id];
    const value = Array.isArray(values) ? Number(values[yearIndex]) : NaN;
    return Number.isFinite(value) ? value : base;
  };
  const merit = get('meritIncreasePct', 3.5);
  const incentive = get('incentiveAccrualPct', 18.0);
  const productivity = get('productivityLiftPct', 1.5);
  const loanRetention = get('loanRetentionPct', 94.0);
  const depositRetention = get('depositRetentionPct', 92.0);
  return {
    expense: compBase * ((merit - 3.5) / 100) + compBase * 0.22 * ((incentive - 18.0) / 100),
    loanPts: (productivity - 1.5) * 0.18 + (loanRetention - 94.0) * 0.06,
    depositPts: (productivity - 1.5) * 0.12 + (depositRetention - 92.0) * 0.07
  };
};

const finiteBalance = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const openingBalanceForAdjustment = (row, previousRow, openingKey, endingKey) =>
  finiteBalance(row?.[openingKey])
  ?? finiteBalance(previousRow?.[endingKey])
  ?? finiteBalance(row?.[endingKey])
  ?? 0;

// Driver-based non-interest revenue: ratio (bps annual) applied to a model
// balance basis, with per-period manual overlays (add to / replace the
// driver-computed amount for a given forecast month).
export const FEE_DRIVER_DEFS = [
  {
    id: 'treasuryFees',
    label: 'Treasury mgmt fees',
    basis: 'avgDeposits',
    basisLabel: '% of avg deposits',
    defaultRatioBps: 0,
    glGroup: 'Other Service Charges'
  },
  {
    id: 'commercialFees',
    label: 'Commercial banking fees',
    basis: 'newLoanProduction',
    basisLabel: '% of new commercial loan production',
    defaultRatioBps: 0,
    glGroup: 'Deposit Service Charges'
  },
  {
    id: 'wealthFees',
    label: 'Wealth / trust fees',
    basis: 'avgLoans',
    basisLabel: '% of avg earning assets',
    defaultRatioBps: 0,
    glGroup: 'Trust and Investment Services'
  }
];

export const DEFAULT_FEE_DRIVERS = FEE_DRIVER_DEFS.map((def) => ({
  id: def.id,
  ratioBps: ADJUSTMENT_YEARS.map(() => def.defaultRatioBps),
  overlays: [] // {monthIndex (0-59), mode: 'add' | 'replace', amountMM, note}
}));

const monthBasis = (row, basis, newProductionMonthly, monthIndex) => {
  if (basis === 'avgDeposits') return row.averageDeposits;
  if (basis === 'avgLoans') return row.averageLoans;
  if (basis === 'newLoanProduction') return (newProductionMonthly[monthIndex] || {}).loans || 0;
  return 0;
};

export const computeFeeDriverMonthly = (driverState, monthlyRows, newProductionMonthly) => {
  // Returns per-driver arrays of monthly $MM plus the total per month.
  const perDriver = {};
  const total = monthlyRows.map(() => 0);
  FEE_DRIVER_DEFS.forEach((def) => {
    const state = driverState.find((d) => d.id === def.id);
    if (!state) return;
    const series = monthlyRows.map((row, index) => {
      const yearIndex = Math.min(Math.floor(index / 12), ADJUSTMENT_YEARS.length - 1);
      const ratio = (state.ratioBps[yearIndex] || 0) / 10000;
      const basisValue = monthBasis(row, def.basis, newProductionMonthly, index);
      let amount = def.basis === 'newLoanProduction'
        ? basisValue * ratio              // production is already a monthly flow
        : (basisValue * ratio) / 12;      // balances earn the annual ratio over 12 months
      const overlay = state.overlays.find((o) => o.monthIndex === index);
      if (overlay) {
        amount = overlay.mode === 'replace' ? overlay.amountMM : amount + overlay.amountMM;
      }
      return amount;
    });
    perDriver[def.id] = series;
    series.forEach((value, index) => { total[index] += value; });
  });
  return { perDriver, total };
};

export const buildNewProductionMonthly = (hireRows, officerTypes, typeInputs, macro) => {
  // Monthly going-on production from individual hire rows.
  // Each row: {id, typeId, fte, startMonth (1-60), assumptions{...}, perYear{annualLoan?, annualDeposit?, annualFee?}}
  const months = Array.from({ length: 60 }, () => ({
    loans: 0, deposits: 0, fees: 0, expense: 0, byType: {}
  }));
  hireRows.forEach((row) => {
    const type = officerTypes.find((t) => t.id === row.typeId);
    if (!type || !row.fte) return;
    const input = typeInputs[row.typeId] || {};
    const vacancy = (row.assumptions?.vacancyOverride ?? input.vacancyOverride ?? type.vacancy) / 100;
    const attrition = (row.assumptions?.attritionOverride ?? input.attritionOverride ?? type.attrition) / 100;
    const ramp = Math.max(1, row.assumptions?.rampMonths ?? type.rampMonths);
    const start = Math.max(1, Math.min(60, row.startMonth || 1));
    const demand = (macro.loanDemand || 1) * Math.min(1.08, Math.max(0.9, 1 + macro.gdp / 140));
    const creditHaircut = Math.min(1, Math.max(0.88, 1 - macro.creditLoss / 100));
    for (let m = start - 1; m < 60; m += 1) {
      const monthsActive = m - (start - 1) + 1;
      const rampFactor = Math.min(1, monthsActive / ramp);
      const survival = Math.pow(1 - attrition / 12, monthsActive);
      const effFte = row.fte * (1 - vacancy) * survival;
      const yearIndex = Math.min(Math.floor(m / 12), ADJUSTMENT_YEARS.length - 1);
      const annualLoan = row.perYear?.annualLoan?.[yearIndex] ?? row.assumptions?.annualLoan ?? type.annualLoan;
      const annualDeposit = row.perYear?.annualDeposit?.[yearIndex] ?? row.assumptions?.annualDeposit ?? type.annualDeposit;
      const annualFee = row.perYear?.annualFee?.[yearIndex] ?? row.assumptions?.annualFee ?? type.annualFee;
      const annualExpense = row.assumptions?.annualExpense ?? type.annualExpense;
      const cell = months[m];
      const loanFlow = (effFte * annualLoan * rampFactor * demand * creditHaircut) / 12;
      const depositFlow = (effFte * annualDeposit * rampFactor) / 12;
      cell.loans += loanFlow;
      cell.deposits += depositFlow;
      cell.fees += (effFte * annualFee * rampFactor) / 12;
      cell.expense += (effFte * annualExpense) / 12;
      const byType = cell.byType[row.typeId] || { loans: 0, deposits: 0, fte: 0 };
      byType.loans += loanFlow;
      byType.deposits += depositFlow;
      byType.fte += effFte / 60;
      cell.byType[row.typeId] = byType;
    }
  });
  return months;
};

export const applyYearAdjustments = (monthlyRows, yearAdjustments, feeDriverState,
                                     newProductionMonthly, taxRatePct,
                                     workforceYears = null, workforceCompBase = 0,
                                     quarterlyGrowth = null) => {
  const { total: driverFees, perDriver } = computeFeeDriverMonthly(
    feeDriverState, monthlyRows, newProductionMonthly
  );
  let loanFactor = 1;
  let depositFactor = 1;
  const adjusted = monthlyRows.map((row, index) => {
    const yearIndex = Math.min(Math.floor(index / 12), ADJUSTMENT_YEARS.length - 1);
    const quarterIndex = Math.floor(((Number(row.monthNumber) || (index + 1)) - 1) / 3) % 4;
    const workforce = workforceYearEffects(workforceYears, yearIndex, workforceCompBase);
    const year = Number(row.fiscalYearNumber) || ADJUSTMENT_YEARS[yearIndex];
    const loanPts = resolveForecastQuarterAdjustment({
      quarterlyGrowth,
      leverId: 'loanGrowthPts',
      year,
      quarterIndex,
      annualTarget: yearAdjustments.loanGrowthPts[yearIndex] || 0
    }) + workforce.loanPts;
    const depositPts = resolveForecastQuarterAdjustment({
      quarterlyGrowth,
      leverId: 'depositGrowthPts',
      year,
      quarterIndex,
      annualTarget: yearAdjustments.depositGrowthPts[yearIndex] || 0
    }) + workforce.depositPts;
    const previousRow = monthlyRows[index - 1];
    const openingLoans = openingBalanceForAdjustment(
      row, previousRow, 'openingLoans', 'endingLoans'
    ) * loanFactor;
    const openingDeposits = openingBalanceForAdjustment(
      row, previousRow, 'openingDeposits', 'endingDeposits'
    ) * depositFactor;
    loanFactor *= 1 + loanPts / 100 / 12;
    depositFactor *= 1 + depositPts / 100 / 12;

    const endingLoans = row.endingLoans * loanFactor;
    const endingDeposits = row.endingDeposits * depositFactor;
    const averageLoans = row.averageLoans * loanFactor;
    const averageDeposits = row.averageDeposits * depositFactor;
    const interestIncome = averageLoans * (row.loanYield / 100) / 12;
    const interestExpense = averageDeposits * (row.depositCost / 100) / 12;
    const fundingBenefit = row.nii - (row.interestIncome - row.interestExpense);
    const nii = interestIncome - interestExpense + fundingBenefit;

    const nonInterestIncome = row.nonInterestIncome
      + (yearAdjustments.feeAdjMM[yearIndex] || 0) / 12
      + driverFees[index];
    const nonInterestExpense = row.nonInterestExpense
      + (yearAdjustments.expenseAdjMM[yearIndex] || 0) / 12
      + (yearAdjustments.compAdjMM[yearIndex] || 0) / 12
      + workforce.expense / 12;
    const creditProvision = row.creditProvision
      + (yearAdjustments.provisionAdjMM[yearIndex] || 0) / 12;
    const preTaxIncome = nii + nonInterestIncome - nonInterestExpense - creditProvision;
    const taxExpense = Math.max(0, preTaxIncome * (taxRatePct / 100));
    const netIncome = preTaxIncome - taxExpense;

    return {
      ...row,
      openingLoans, openingDeposits,
      endingLoans, endingDeposits, averageLoans, averageDeposits,
      interestIncome, interestExpense, nii,
      nonInterestIncome, nonInterestExpense, creditProvision,
      preTaxIncome, taxExpense, netIncome,
      driverFees: driverFees[index],
      nim: averageLoans > 0 ? ((nii * 12) / averageLoans) * 100 : 0,
      efficiencyRatio: nii + nonInterestIncome > 0
        ? (nonInterestExpense / (nii + nonInterestIncome)) * 100
        : 0
    };
  });
  return { adjusted, driverFees, perDriver };
};

export const hasYearAdjustments = (yearAdjustments, feeDriverState) =>
  Object.values(yearAdjustments).some((values) => values.some((v) => v !== 0))
  || feeDriverState.some((d) => d.ratioBps.some((v) => v !== 0) || d.overlays.length > 0);

// ── Funds Transfer Pricing (prototype single-rate transfer curve) ──────────
// FTP rate = SOFR + spread. Lending margin = interest income − FTP charge on
// average loans; deposit franchise margin = FTP credit on average deposits −
// interest expense; treasury mismatch = GAAP NII − lending − deposit margins.
export const FTP_SPREAD_PCT = 0.8;

// Demo/fallback SOFR when caller omits rate — central settings (OP_02X macroBase.sofr) are authoritative.
export const FALLBACK_SOFR = 4.85;

export const ftpRateFor = (sofr) => (Number(sofr) || FALLBACK_SOFR) + FTP_SPREAD_PCT;

export const splitNiiFtp = ({ interestIncome, interestExpense, nii, averageLoans, averageDeposits }, ftpRatePct, periodsPerYear = 12) => {
  const charge = (averageLoans * (ftpRatePct / 100)) / periodsPerYear;
  const credit = (averageDeposits * (ftpRatePct / 100)) / periodsPerYear;
  const lendingMargin = interestIncome - charge;
  const depositMargin = credit - interestExpense;
  return {
    lendingMargin,
    depositMargin,
    mismatch: nii - lendingMargin - depositMargin
  };
};

// Maps OP_03 categories to monthly-model fields and comparison GL rollups for
// the live three-line chart (actual / starting baseline / adjusted).
export const TREND_CATEGORIES = [
  { id: 'loans', label: 'Loans (EOP)', modelField: 'endingLoans', rollup: 'Balance Sheet', glGroups: ['Loans'] },
  { id: 'deposits', label: 'Deposits (EOP)', modelField: 'endingDeposits', rollup: 'Balance Sheet', glGroups: ['Transaction Deposits', 'Savings Deposits', 'Time Deposits'] },
  { id: 'nii', label: 'NII', modelField: 'nii', rollup: 'P&L', glGroups: null, niiComposite: true },
  { id: 'fees', label: 'Non-interest income', modelField: 'nonInterestIncome', rollup: 'Non-Interest Income', glGroups: null },
  { id: 'expense', label: 'Non-interest expense', modelField: 'nonInterestExpense', rollup: 'Non-Interest Expense', glGroups: null },
  { id: 'provision', label: 'Provision', modelField: 'creditProvision', rollup: 'Provision for Credit Losses', glGroups: null },
  { id: 'netIncome', label: 'Net income', modelField: 'netIncome', rollup: 'P&L', glGroups: null, netComposite: true }
];
